import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sbomApi } from './sbomApi';
import SbomCheck from './SbomCheck';

const STEPS = ['Choose file', 'Read file', 'Check file', 'Release', 'Review changes', 'Create'];
const ACCEPT = '.json,.xml,.spdx,.txt,.xlsx,.csv,.tsv';
const KIND_LABEL = { 'cyclonedx-json': 'CycloneDX (JSON)', 'cyclonedx-xml': 'CycloneDX (XML)', 'spdx-json': 'SPDX (JSON)',
  'spdx-tag': 'SPDX (tag-value)', spreadsheet: 'Spreadsheet' };
const DIFF = { added: ['Added', 'ok'], changed: ['New version', 'info'], removed: ['Not in this file', 'bad'], same: ['Unchanged', 'plain'] };

/**
 * Import SBOM — upload → (spreadsheet: map columns) → check (tree + validation; an error blocks)
 * → release → review → create. Nothing is written to Orcanos before Create (Critical Note #90).
 */
export default function SbomImport({ info, onAuth, importId }) {
  const navigate = useNavigate();
  if (!importId) return <ImportStart onAuth={onAuth} onStarted={(id) => navigate(`/bom/sw-import/${id}`)} />;
  return <ImportWizard key={importId} info={info} onAuth={onAuth} importId={importId} />;
}

/** The step bar. With `onGo`, a finished step (from Read file on) is a button that goes back to it. */
function Steps({ step, onGo }) {
  return (
    <div className="sbom-steps">
      {STEPS.map((l, i) => {
        const cls = `sbom-step${step > i + 1 ? ' sbom-step--done' : step === i + 1 ? ' sbom-step--cur' : ''}`;
        const inner = <><i>{step > i + 1 ? '✓' : i + 1}</i>{l}</>;
        return onGo && i >= 1 && step > i + 1
          ? <button type="button" key={l} className={`${cls} sbom-step--go`} onClick={() => onGo(i + 1)} title={`Back to ${l}`}>{inner}</button>
          : <div key={l} className={cls}>{inner}</div>;
      })}
    </div>
  );
}

function Head() {
  return (
    <div className="sbom-head">
      <div>
        <h2>Import SBOM</h2>
        <div className="sbom-sub">Bring a scanner file or the spreadsheet you keep today. Nothing is written to Orcanos until
          you press Create.</div>
      </div>
    </div>
  );
}

function ImportStart({ onAuth, onStarted }) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [recent, setRecent] = useState(null);
  const input = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    sbomApi.imports().then((d) => setRecent(d.imports || [])).catch((e) => { if (!onAuth(e)) setRecent([]); });
  }, [onAuth]);

  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const st = await sbomApi.upload(file);
      onStarted(st.id);
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Remove an import from the history. One that wrote needs Delete on the release type (server-checked);
  // only the record goes — the Orcanos items and the release's SBOM stay.
  const remove = async (r) => {
    const wrote = r.status !== 'draft';
    if (!window.confirm(wrote
      ? `Delete the import of ${r.file_name} from the history? It wrote to Orcanos: the items and the release's SBOM stay, `
        + 'only this record of the import (and its stored file) is removed. This needs Delete permission on the release type.'
      : `Delete the import of ${r.file_name}? Nothing was written to Orcanos.`)) return;
    setError('');
    try {
      await sbomApi.deleteImport(r.id);
      setRecent((list) => list.filter((x) => x.id !== r.id));
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    }
  };

  return (
    <>
      <Head />
      <Steps step={1} />
      <div className={`sbom-drop${over ? ' sbom-drop--over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files?.[0]); }}>
        <div style={{ fontSize: 28 }} aria-hidden="true">⤒</div>
        <b>{busy ? 'Reading the file…' : 'Drop a file here, or choose one'}</b>
        <div className="sbom-fmts">
          {['CycloneDX JSON', 'CycloneDX XML', 'SPDX JSON', 'SPDX tag-value', 'Excel (.xlsx)', 'CSV'].map((f) => (
            <span key={f} className="sbom-pill sbom-pill--plain">{f}</span>
          ))}
        </div>
        <div className="sbom-tiny sbom-muted">The format is read from the content, not the file name. Up to 20 MB.</div>
        <input ref={input} type="file" accept={ACCEPT} hidden onChange={(e) => send(e.target.files?.[0])} />
        <button type="button" className="sbom-btn sbom-btn--pri" disabled={busy} onClick={() => input.current?.click()}>Choose file</button>
      </div>
      {error && <div className="sbom-error" role="alert" style={{ marginTop: 12 }}>{error}</div>}
      {recent && recent.length > 0 && (
        <div className="sbom-table-card" style={{ marginTop: 16 }}>
          <div className="sbom-tbar"><b>Recent imports</b></div>
          <div className="sbom-tw">
            <table className="sbom-table">
              <thead><tr><th>File</th><th>Release</th><th>Status</th><th className="sbom-hide-s">Started</th><th /></tr></thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id}>
                    <td>{r.file_name}</td>
                    <td>{[r.target?.name, r.target?.version].filter(Boolean).join(' ') || '—'}</td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="sbom-hide-s sbom-mono">{String(r.created_at || '').slice(0, 16)} UTC</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="sbom-act" onClick={() => navigate(`/bom/sw-import/${r.id}`)}>Open</button>
                      {r.status !== 'creating' && (
                        <button type="button" className="sbom-act sbom-act--del" onClick={() => remove(r)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function StatusPill({ status }) {
  const m = { draft: ['Not created yet', 'plain'], creating: ['Creating…', 'info'], done: ['In Orcanos', 'ok'], failed: ['Finished with errors', 'bad'] };
  const [l, t] = m[status] || [status, 'plain'];
  return <span className={`sbom-pill sbom-pill--${t}`}>{l}</span>;
}

function stepOf(st) {
  if (!st) return 2;
  if (['creating', 'done', 'failed'].includes(st.status)) return 6;
  if (st.needs_mapping) return 2;
  if (!st.target?.mode) return 3;
  return 5;
}

function ImportWizard({ info, onAuth, importId }) {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);

  const adopt = useCallback((d, move = true) => {
    setSt(d);
    if (move) setStep(stepOf(d));
  }, []);

  useEffect(() => {
    sbomApi.getImport(importId).then((d) => adopt(d)).catch((e) => { if (!onAuth(e)) setError(e.message); });
  }, [importId, adopt, onAuth]);

  // Poll ONLY while a create job runs.
  const running = st?.status === 'creating' || st?.job?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      sbomApi.getImport(importId).then((d) => adopt(d, false)).catch(() => {});
    }, 1500);
    return () => clearInterval(t);
  }, [running, importId, adopt]);

  const act = async (fn, after) => {
    setBusy(true);
    setError('');
    try {
      const d = await fn();
      adopt(d, after === undefined);
      if (after) setStep(after);
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!st) return <><Head />{error ? <div className="sbom-error">{error}</div> : <div className="sbom-muted">Loading…</div>}</>;
  const cur = step || stepOf(st);
  const restart = async () => {
    try { await sbomApi.deleteImport(st.id); } catch { /* keep going */ }
    navigate('/bom/sw-import');
  };
  // Read the stored file again as a NEW import (the old one stays; a release it created is reused).
  const reload = async () => {
    setBusy(true);
    setError('');
    try {
      const d = await sbomApi.reload(st.id);
      navigate(`/bom/sw-import/${d.id}`);
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Head />
      <Steps step={cur} onGo={st.status === 'draft' && !busy ? setStep : null} />
      {error && <div className="sbom-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
      {cur === 2 && <FileStep st={st} busy={busy} act={act} onNext={() => setStep(3)} onRestart={restart} />}
      {cur === 3 && <CheckStep st={st} busy={busy} onBack={() => setStep(2)} onNext={() => setStep(4)} onRestart={restart} onReload={reload} />}
      {cur === 4 && <ReleaseStep st={st} busy={busy} act={act} onBack={() => setStep(3)} onAuth={onAuth} />}
      {cur === 5 && <ReviewStep st={st} busy={busy} onBack={() => setStep(4)}
        onCreate={() => act(() => sbomApi.create(st.id), 6)} componentLabel={info.settings.componentLabel} />}
      {cur === 6 && <CreateStep st={st} busy={busy} onOpen={() => navigate('/bom/sw-releases')}
        onReload={reload}
        onReopen={() => act(() => sbomApi.reopen(st.id))}
        onStop={() => window.confirm('Stop this import? The component being written now is finished first. '
          + 'What is already in Orcanos stays; Reload the file finishes the import later.') && act(() => sbomApi.stop(st.id))} />}
    </>
  );
}

function Summary({ s, fileName }) {
  return (
    <dl className="sbom-kv">
      <dt>File</dt><dd className="sbom-mono">{fileName}</dd>
      <dt>Format</dt><dd>{s.format}{s.spec_version ? ` ${s.spec_version}` : ''}</dd>
      {s.tool && <><dt>Made by</dt><dd>{s.tool}</dd></>}
      {(s.root?.name || s.root?.version) && <><dt>Describes</dt><dd>{[s.root.name, s.root.version].filter(Boolean).join(' ')}</dd></>}
      <dt>Components</dt><dd>{s.components} ({s.direct} direct)</dd>
      <dt>Dependencies</dt>
      <dd>{s.has_dependencies ? 'Yes. The tree is built from the file.'
        : 'None in the file. Every component goes directly under the release.'}</dd>
    </dl>
  );
}

function FileStep({ st, busy, act, onNext, onRestart }) {
  const [mapping, setMapping] = useState(st.mapping || {});
  const lay = st.layout;
  useEffect(() => { setMapping(st.mapping || {}); }, [st.mapping]);

  if (st.kind !== 'spreadsheet') {
    return (
      <div className="sbom-panel">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <b>{KIND_LABEL[st.kind] || st.kind}</b><span className="sbom-pill sbom-pill--ok">Read</span>
        </div>
        <Summary s={st.summary} fileName={st.file_name} />
        {(st.summary.warnings || []).map((w) => <div key={w} className="sbom-warn">{w}</div>)}
        <div className="sbom-actions">
          <button type="button" className="sbom-btn" onClick={onRestart}>Choose another file</button>
          <button type="button" className="sbom-btn sbom-btn--pri" onClick={onNext}>Continue</button>
        </div>
      </div>
    );
  }

  const roleOf = (i) => mapping[String(i)] || '';
  const setRole = (i, r) => {
    const next = { ...mapping };
    Object.keys(next).forEach((k) => { if (r && next[k] === r) delete next[k]; });   // one column per field
    if (r) next[String(i)] = r; else delete next[String(i)];
    setMapping(next);
  };
  const hasName = Object.values(mapping).includes('name');

  return (
    <div className="sbom-panel">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <b className="sbom-mono">{st.file_name}</b><span className="sbom-pill sbom-pill--ok">Read</span>
        <span className="sbom-tiny sbom-muted">{lay.rows} rows</span>
      </div>
      <div className="sbom-grid2">
        <label className="sbom-field">Sheet
          <select className="sbom-select" value={lay.sheet} disabled={busy}
            onChange={(e) => act(() => sbomApi.setLayout(st.id, e.target.value, 0), 2)}>
            {lay.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="sbom-field">Header row
          <input className="sbom-input" type="number" min="1" defaultValue={lay.header_row + 1} disabled={busy}
            onBlur={(e) => {
              const n = Math.max(1, parseInt(e.target.value, 10) || 1) - 1;
              if (n !== lay.header_row) act(() => sbomApi.setLayout(st.id, lay.sheet, n), 2);
            }} />
        </label>
      </div>
      <h4 style={{ margin: '4px 0 0' }}>Map columns</h4>
      <div className="sbom-tiny sbom-muted">Proposed from the column titles. Change any line. A column of known CVEs is never
        imported; vulnerabilities are checked separately.</div>
      <div className="sbom-tw">
        <table className="sbom-table">
          <thead><tr><th>Column in your file</th><th>Example</th><th>Orcanos field</th></tr></thead>
          <tbody>
            {lay.headers.map((h, i) => (
              <tr key={i}>
                <td><b>{h || `Column ${i + 1}`}</b></td>
                <td className="sbom-mono sbom-muted" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{lay.samples[i]}</td>
                <td>
                  <select className="sbom-select" style={{ width: 'auto' }} value={roleOf(i)} aria-label={`Field for ${h}`}
                    onChange={(e) => setRole(i, e.target.value)}>
                    <option value="">Not imported</option>
                    {st.roles.map((r) => <option key={r} value={r}>{st.role_labels[r]}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sbom-note">Rows without a Package URL get one built from the name, version and ecosystem. A
        <b> Parent</b> column builds the tree by name; without one the list is flat.</div>
      {st.summary && <Summary s={st.summary} fileName={st.file_name} />}
      {(st.summary?.warnings || []).map((w) => <div key={w} className="sbom-warn">{w}</div>)}
      <div className="sbom-actions">
        <button type="button" className="sbom-btn" onClick={onRestart}>Choose another file</button>
        <button type="button" className="sbom-btn sbom-btn--pri" disabled={busy || !hasName}
          onClick={() => act(() => sbomApi.setMapping(st.id, mapping), 3)}>
          {hasName ? 'Read the rows and check them' : 'Choose the Name column'}
        </button>
      </div>
    </div>
  );
}

function CheckStep({ st, busy, onBack, onNext, onRestart, onReload }) {
  const ok = st.validation?.ok;
  return (
    <div className="sbom-panel">
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b>Check the file before anything is saved to Orcanos</b>
        <span className="sbom-tiny sbom-muted sbom-mono">{st.file_name}</span>
      </div>
      {st.summary && <Summary s={st.summary} fileName={st.file_name} />}
      {st.preview && st.validation ? <SbomCheck preview={st.preview} validation={st.validation} />
        : st.summary
          ? <div className="sbom-error" role="alert">The server did not return the file check. It may be running an older
            version: restart it and open this import again.</div>
          : <div className="sbom-muted">The file has not been read yet.</div>}
      <div className="sbom-actions">
        <button type="button" className="sbom-btn" onClick={onBack}>Back</button>
        <button type="button" className="sbom-btn" onClick={onRestart}>Choose another file</button>
        <button type="button" className="sbom-btn" disabled={busy} onClick={onReload}
          title="Read the same file again, as a new import">Reload the file</button>
        <button type="button" className="sbom-btn sbom-btn--pri" disabled={!ok} onClick={onNext}
          title={ok ? '' : 'Fix the errors in the file and import it again'}>
          {ok ? 'Continue' : st.validation ? 'Blocked by errors' : 'Continue'}</button>
      </div>
    </div>
  );
}

function ReleaseStep({ st, busy, act, onBack, onAuth }) {
  const t = st.target || {};
  const [releases, setReleases] = useState(null);
  const [mode, setMode] = useState(t.mode || 'new');
  const [existing, setExisting] = useState(t.release_item_id || '');
  // An Excel file names no product, so start from the file name (without its extension) instead of an empty box.
  const fileStem = String(st.file_name || '').replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').trim();
  const [name, setName] = useState(t.name || st.summary?.root?.name || fileStem);
  const [version, setVersion] = useState(t.version || st.summary?.root?.version || '');
  const [compareWith, setCompareWith] = useState(t.compare_with || null);

  useEffect(() => {
    sbomApi.releases().then((d) => {
      const list = d.releases || [];
      setReleases(list);
      setCompareWith((cur) => (cur !== null ? cur : (list.find((r) => r.imported)?.item_id || '')));
    }).catch((e) => { if (!onAuth(e)) setReleases([]); });
  }, [onAuth]);

  const chosen = releases?.find((r) => r.item_id === existing);
  // One release per product + version: a "new" one that already exists is an update of it.
  const norm = (x) => String(x || '').split(/\s+/).filter(Boolean).join(' ').toLowerCase();
  const want = norm(`${name} ${version}`);
  // Several Orcanos items can share the name (a leftover from an earlier run): prefer the one with an SBOM.
  const twins = mode === 'new' && want ? (releases || []).filter((r) => norm(r.name) === want) : [];
  const twin = twins.find((r) => r.imported) || twins[0] || null;
  const sameFile = st.duplicate?.same_file;
  const blocked = (mode === 'new' && !!twin) || (mode === 'existing' && sameFile && sameFile.item_id === existing);
  const ok = (mode === 'new' ? !!name.trim() : !!existing) && !blocked;
  const useTwin = () => { setMode('existing'); setExisting(twin.item_id); };

  return (
    <div className="sbom-panel">
      <b>Which release does this file describe?</b>
      {sameFile && (
        <div className="sbom-warn" role="status">
          This file is already the SBOM of <b>{sameFile.name}</b>{sameFile.key ? ` (${sameFile.key})` : ''}, imported
          {' '}{String(sameFile.imported_at || '').slice(0, 10)} UTC. There is nothing new to import.
        </div>
      )}
      <div role="radio" aria-checked={mode === 'new'} tabIndex={0} className={`sbom-opt${mode === 'new' ? ' sbom-opt--on' : ''}`}
        onClick={() => setMode('new')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMode('new'); }}>
        <div style={{ flex: 1 }}>
          <b>A new release</b>
          <div className="sbom-tiny sbom-muted">Creates a Software Release item in Orcanos.</div>
          {mode === 'new' && (
            <div className="sbom-grid2" style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
              <label className="sbom-field">Name<input className="sbom-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lab Analyzer" /></label>
              <label className="sbom-field">Version<input className="sbom-input" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 2.4" /></label>
            </div>
          )}
          {twin && (
            <div className="sbom-warn" style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
              {twin.imported ? (
                <><b>{twin.name}</b>{twin.key ? ` (${twin.key})` : ''} already exists. A release is imported once; a new file
                  for it updates it.{' '}</>
              ) : (
                <><b>{twin.name}</b>{twin.key ? ` (${twin.key})` : ''} is already in Orcanos, with no SBOM here (it was deleted
                  from the Software BOM, or made in Orcanos). Import this file into it, so Orcanos does not get a second
                  release item.{' '}</>
              )}
              <button type="button" className="sbom-btn" onClick={useTwin}>
                {twin.imported ? 'Update that release' : 'Import into that release'}</button>
            </div>
          )}
        </div>
      </div>
      <div role="radio" aria-checked={mode === 'existing'} tabIndex={0} className={`sbom-opt${mode === 'existing' ? ' sbom-opt--on' : ''}`}
        onClick={() => setMode('existing')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMode('existing'); }}>
        <div style={{ flex: 1 }}>
          <b>An existing release</b>
          <div className="sbom-tiny sbom-muted">Its SBOM is updated from this file. The current tree is kept as a baseline you
            can compare with. Components already in Orcanos are linked, not copied.</div>
          {mode === 'existing' && (
            <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
              <select className="sbom-select" value={existing} onChange={(e) => setExisting(e.target.value)} aria-label="Release">
                <option value="">Choose a release…</option>
                {(releases || []).map((r) => (
                  <option key={r.item_id} value={r.item_id}>{r.name || r.item_id}{r.key ? ` (${r.key})` : ''}{r.imported ? '' : ' — no SBOM yet'}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
      {mode === 'existing' && chosen?.imported ? (
        <div className="sbom-tiny sbom-muted">Compared with its current SBOM.</div>
      ) : (
        <label className="sbom-field" style={{ maxWidth: 380 }}>Compare with
          <select className="sbom-select" value={compareWith || ''} onChange={(e) => setCompareWith(e.target.value)}>
            <option value="">Nothing (everything shows as added)</option>
            {(releases || []).filter((r) => r.imported && r.item_id !== existing).map((r) => (
              <option key={r.item_id} value={r.item_id}>{r.name}</option>
            ))}
          </select>
        </label>
      )}
      <div className="sbom-actions">
        {!ok && (
          <span className="sbom-tiny sbom-muted" role="status" style={{ marginRight: 'auto', alignSelf: 'center' }}>
            {blocked ? 'Use the button above to pick that release.'
              : mode === 'new' ? 'Type the release name to continue.' : 'Choose a release to continue.'}
          </span>
        )}
        <button type="button" className="sbom-btn" onClick={onBack}>Back</button>
        <button type="button" className="sbom-btn sbom-btn--pri" disabled={busy || !ok}
          onClick={() => act(() => sbomApi.setTarget(st.id, {
            mode, release_item_id: existing, key: chosen?.key || '', name: mode === 'new' ? name : (chosen?.name || ''),
            version: mode === 'new' ? version : '', compare_with: compareWith || '',
          }), 5)}>Review changes</button>
      </div>
    </div>
  );
}

function ReviewStep({ st, busy, onBack, onCreate, componentLabel }) {
  const [tab, setTab] = useState('changes');
  const rv = st.review;
  if (!rv) return <div className="sbom-muted">Preparing the review…</div>;
  const rows = rv.rows.filter((r) => (tab === 'changes' ? r.kind !== 'same' : tab === 'all' ? true : r.kind === tab));
  const t = st.target;
  return (
    <div className="sbom-panel">
      <b>{t.mode === 'new' ? `New release: ${[t.name, t.version].filter(Boolean).join(' ')}` : `Update the SBOM of ${t.name}`}</b>
      {t.mode === 'existing' && rv.compare_with === t.release_item_id && (
        <div className="sbom-note">Its current tree is saved as a baseline before it is replaced.</div>
      )}
      {st.duplicate?.exists && (
        <div className="sbom-error" role="alert">{st.duplicate.exists.name} already exists. Go back and choose it as an
          existing release to update it.</div>
      )}
      {st.duplicate?.same_file && t.mode === 'existing' && st.duplicate.same_file.item_id === t.release_item_id && (
        <div className="sbom-error" role="alert">This file is already the SBOM of {t.name}. Nothing to update.</div>
      )}
      <div className="sbom-diffn">
        <div className="sbom-stat"><div className="sbom-stat-n" style={{ color: '#1F7A52' }}>+{rv.counts.added}</div><div className="sbom-stat-l">Added</div></div>
        <div className="sbom-stat"><div className="sbom-stat-n" style={{ color: '#2F5D9E' }}>↑{rv.counts.changed}</div><div className="sbom-stat-l">New version</div></div>
        <div className="sbom-stat"><div className="sbom-stat-n" style={{ color: '#B42318' }}>−{rv.counts.removed}</div><div className="sbom-stat-l">Not in this file</div></div>
        <div className="sbom-stat"><div className="sbom-stat-n">{rv.counts.same}</div><div className="sbom-stat-l">Unchanged</div></div>
      </div>
      {!rv.compare_with && <div className="sbom-note">Not compared with another release, so every package shows as added.</div>}
      <div className="sbom-tabs">
        {[['changes', 'Changes'], ['added', 'Added'], ['changed', 'New version'], ['removed', 'Not in this file'], ['all', 'All']].map(([k, l]) => (
          <button type="button" key={k} className={tab === k ? 'sbom-on' : ''} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      <div className="sbom-tw" style={{ maxHeight: 360, overflowY: 'auto' }}>
        <table className="sbom-table">
          <thead><tr><th>Change</th><th>Package</th><th>Before</th><th>This file</th></tr></thead>
          <tbody>
            {rows.slice(0, 1000).map((r) => (
              <tr key={`${r.kind}|${r.key || r.package}`}>
                <td><span className={`sbom-pill sbom-pill--${DIFF[r.kind][1]}`}>{DIFF[r.kind][0]}</span></td>
                <td>{r.package}</td><td className="sbom-mono">{r.before || '—'}</td><td className="sbom-mono">{r.after || '—'}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="sbom-empty">Nothing in this list.</td></tr>}
          </tbody>
        </table>
      </div>
      {(st.summary?.warnings || []).map((w) => <div key={w} className="sbom-warn">{w}</div>)}
      <div className="sbom-note">
        Creates up to <b>{rv.plan.create}</b> {componentLabel || 'Software Component'} item{rv.plan.create === 1 ? '' : 's'}
        {rv.plan.create ? ' (any found in Orcanos through the Component filter are reused instead)' : ''}
        {t.mode === 'new' ? ' and 1 release' : ''}{rv.plan.link ? <>, reuses <b>{rv.plan.link}</b> already in Orcanos</> : null}. One item per
        package version, however many places use it; the tree is kept in the Software BOM. Then it attaches <span className="sbom-mono">{st.file_name}</span> to it and saves the tree.
      </div>
      <div className="sbom-actions">
        <button type="button" className="sbom-btn" onClick={onBack}>Back</button>
        <button type="button" className="sbom-btn sbom-btn--pri" disabled={busy || st.validation?.ok === false || !!st.duplicate?.exists
          || (t.mode === 'existing' && st.duplicate?.same_file?.item_id === t.release_item_id)}
          onClick={onCreate}>{t.mode === 'existing' ? 'Update in Orcanos' : 'Create in Orcanos'}</button>
      </div>
    </div>
  );
}

// The job's messages are "<package version>: <reason>" per component, so one wrong field mapping
// repeats the same reason for every component. Group by reason: one line per cause, with the
// packages it hit under it; a message nothing else shares is shown whole.
function groupProblems(errors) {
  const groups = new Map();
  errors.forEach((e) => {
    const i = e.indexOf(': ');
    const reason = i > 0 ? e.slice(i + 2) : e;
    if (!groups.has(reason)) groups.set(reason, []);
    groups.get(reason).push({ name: i > 0 ? e.slice(0, i) : '', full: e });
  });
  return [...groups.entries()].map(([reason, items]) => (items.length > 1
    ? { text: reason, names: items.map((x) => x.name) }
    : { text: items[0].full, names: [] }));
}

function ImportProblems({ errors }) {
  const groups = groupProblems(errors);
  return (
    <div className="sbom-problems">
      <b className="sbom-tiny">{groups.length === 1 ? 'What went wrong' : `${groups.length} problems`}</b>
      {groups.map((g) => (
        <div key={g.text} className="sbom-problem">
          <div>{g.text}</div>
          {g.names.length > 0 && (
            <details>
              <summary>{g.names.length} components</summary>
              <div className="sbom-problem-names">{g.names.join(', ')}</div>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}

function CreateStep({ st, busy, onOpen, onReload, onReopen, onStop }) {
  const j = st.job || {};
  const r = st.result || {};
  const running = st.status === 'creating' && j.running !== false;
  const total = j.total || r.total || 0;
  const done = running ? (j.done || 0) : total;
  const pct = total ? Math.round((done / total) * 100) : (running ? 5 : 100);
  const src = running ? j : r;
  const errors = (running ? j.errors : r.errors) || [];
  const interrupted = st.status === 'creating' && !st.job;
  const wrote = !!(st.release_item_id || r.created || r.traces || r.attached);
  return (
    <div className="sbom-panel">
      <b>{running ? (j.stop ? 'Stopping…' : 'Creating in Orcanos…') : st.status === 'done' ? 'Imported'
        : r.stopped ? 'Stopped by you' : st.status === 'failed' ? 'Finished with errors' : 'Stopped'}</b>
      {interrupted && <div className="sbom-warn">This import was interrupted (the server restarted). Components already
        created are found again by their package URL, so reloading the file does not create them twice.</div>}
      <div className="sbom-prog"><i style={{ width: `${pct}%` }} /></div>
      <div className="sbom-tiny sbom-muted">{running ? `${j.stage || ''} · ${done} of ${total} components` : `${total} components`}</div>
      <dl className="sbom-kv">
        <dt>Created</dt><dd>{src.created || 0}</dd>
        <dt>Reused (already in Orcanos)</dt><dd>{src.linked || 0}{src.found ? ` (${src.found} found by the Component filter)` : ''}</dd>
        {!running && (r.traces != null || r.trace_failed) && <><dt>Traced to the release</dt>
          <dd>{r.traces || 0}{r.trace_failed ? ` (${r.trace_failed} refused)` : ''}</dd></>}
        {!running && <><dt>File attached</dt><dd>{r.attached ? 'Yes' : 'No'}</dd></>}
        {!running && r.baseline && <><dt>Previous tree</dt><dd>Kept as a baseline (Compare releases)</dd></>}
        {src.failed ? <><dt>Failed</dt><dd>{src.failed}</dd></> : null}
      </dl>
      {errors.length > 0 && <ImportProblems errors={errors} />}
      <div className="sbom-tiny sbom-muted">Runs on the server. You can leave this page and come back to it from Import SBOM.</div>
      {running && (
        <div className="sbom-actions">
          <button type="button" className="sbom-btn" disabled={busy || !!j.stop} onClick={onStop}>
            {j.stop ? 'Stopping…' : 'Stop import'}</button>
        </div>
      )}
      {!running && st.status !== 'done' && (
        wrote
          ? <div className="sbom-note">Part of this import is already in Orcanos, so it is kept as the record of what was
            written. <b>Reload the file</b> starts a new import of the same file{st.release_item_id ? ' into the same release' : ''};
            components already created are linked, not created again.</div>
          : <div className="sbom-note">Nothing was written to Orcanos. Fix the cause (for example in Settings → Software BOM),
            then go back to the steps and press Create again, or reload the file.</div>
      )}
      {!running && (
        <div className="sbom-actions">
          {st.status !== 'done' && !wrote && (
            <button type="button" className="sbom-btn sbom-btn--pri" disabled={busy} onClick={onReopen}>Go back to the steps</button>
          )}
          {st.status !== 'done' && (
            <button type="button" className="sbom-btn" disabled={busy} onClick={onReload}>Reload the file</button>
          )}
          <button type="button" className={`sbom-btn${st.status === 'done' ? ' sbom-btn--pri' : ''}`} onClick={onOpen}>Open software releases</button>
        </div>
      )}
    </div>
  );
}
